// src/components/FeedScopeSelect.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown } from "phosphor-react";

export default function FeedScopeSelect({ value, onChange, circles = [] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const options = useMemo(() => {
    const base = [
      { value: "following", label: "Following" },
      { value: "mine", label: "My activities" },
    ];
    const circleOpts = circles.map((c) => ({
      value: `circle:${c.id}`,
      label: c.name,
    }));
    return [...base, ...circleOpts];
  }, [circles]);

  const currentLabel =
    options.find((o) => o.value === value)?.label || "Following";

  // close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const selectOption = (val) => {
    onChange?.(val);
    setOpen(false);
  };

  return (
    <div
      ref={ref}
      className={`relative w-fit ${open ? "z-[100]" : "z-auto"}`}
    >
      {/* Trigger pill (blue-300) */}
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="
          inline-flex items-center gap-2
          bg-blue-200 text-blue-800 instrument-font font-bold text-[14px]
          rounded-full !min-h-0 !min-w-0 px-4 py-2 
          border-0 outline-none focus:outline-none focus:ring-0
          shadow-none
        "
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {currentLabel}
        <CaretDown
          size={16}
          weight="bold"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown: opens to the RIGHT, no gap under button */}
      {open && (
        <ul
          role="listbox"
          className="
            absolute left-0 top-full z-[100] w-64
            overflow-hidden rounded-3xl border border-blue-100
            bg-white shadow-lg
          "
        >
          {options.map((o) => {
            const selected = o.value === value;
            return (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => selectOption(o.value)}
                  className={`
                    w-full text-left px-4 py-2 text-[14px] font-medium
                    ${selected ? "bg-blue-200 text-blue-800" : "text-gray-800"}
                    hover:bg-blue-50
                  `}
                >
                  {o.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
